import { $ } from "kw/api";
import { useAPI } from "kw/api/useAPI";
import { handleErrorDisplay } from "kw/util/utils";
import { useState } from "react";
import { TCybersourceMetadata, TStripeMetadata } from "./types";

interface IUsePaymentArgs {
  riskProfileId?: number | string;
  onSubmit?: () => void;
}

export function usePayment(args: IUsePaymentArgs) {
  const { riskProfileId, onSubmit } = args;
  const { mutation, query, loading } = useAPI();
  const [disableRepayment, setDisableRepayment] = useState(false);
  const [paymentBreakdown, setPaymentBreakdown] = useState<object | null>(null);
  const completePaymentMutation = {
    completePayment: [
      {
        input: $("input", "CompletePaymentInput"),
      },
      {
        paymentSuccess: true,
        bindSuccess: true,
        errorMessage: true,
      },
    ],
  };
  const updatePaymentMethodMutation = {
    updatePaymentMethod: [
      {
        input: $("input", "UpdatePaymentMethodInput"),
      },
      {
        success: true,
        errorMessage: true,
      },
    ],
  };
  const submit = (metadata: TStripeMetadata | TCybersourceMetadata) => {
    mutation(
      completePaymentMutation,
      (response: {
        completePayment: {
          paymentSuccess: boolean;
          bindSuccess: boolean;
          errorMessage: string;
        };
      }) => {
        if (!response.completePayment.paymentSuccess) {
          const defaultErrorMessage =
            "Payment processing failed. Please check your payment details and try" +
            " again.";
          const providedErrorMessage = response.completePayment.errorMessage;
          handleErrorDisplay(
            Error("Payment error"),
            providedErrorMessage ? providedErrorMessage : defaultErrorMessage
          );
        } else if (!response.completePayment.bindSuccess) {
          setDisableRepayment(true);
          handleErrorDisplay(
            Error("Policy bind error"),
            "Your payment was successful, but we encountered an issue when creating" +
              " your policy. You don't have to do anything, we will send you your" +
              " policy documents by email when they are ready."
          );
        } else {
          onSubmit?.();
        }
      },
      {
        input: {
          riskProfileId,
          metadata,
        },
      }
    );
  };
  const submitPaymentMethod = (metadata: TStripeMetadata | TCybersourceMetadata) => {
    mutation(
      updatePaymentMethodMutation,
      (response: {
        updatePaymentMethod: {
          success: boolean;
          errorMessage: string;
        };
      }) => {
        if (!response.updatePaymentMethod.success) {
          const defaultErrorMessage =
            "Could not update payment method. Please check your payment details and try";
          const providedErrorMessage = response.updatePaymentMethod.errorMessage;
          handleErrorDisplay(
            Error("Payment error"),
            providedErrorMessage ? providedErrorMessage : defaultErrorMessage
          );
        } else {
          onSubmit?.();
        }
      },
      {
        input: {
          metadata,
        },
      }
    );
  };

  const getPaymentBreakdown = (paymentMethodId) => {
    query(
      {
        paymentBreakdown: [
          {
            riskProfileId: riskProfileId,
            paymentMethodId: paymentMethodId,
          },
          {
            premium: true,
            surcharge: true,
            total: true,
          },
        ],
      },
      (response: { paymentBreakdown }) => {
        setPaymentBreakdown(response.paymentBreakdown);
      }
    );
  };

  return {
    submit,
    submitPaymentMethod,
    getPaymentBreakdown,
    loading,
    disableRepayment,
    paymentBreakdown,
  };
}
